import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import axios from 'axios';
import ClipLoader from 'react-spinners/ClipLoader';
import './Experiments.css';
import CONFIG from '../config.js';

const Experiments = () => {
    const { user, fetchData, setFetchData, isAdmin, adminSelectedUser } = useContext(AuthContext);
    let userInfo = user;
    if (isAdmin) {
        userInfo = adminSelectedUser;
    }
    const [loading, setLoading] = useState(true);
    const [interventionStartDate, setInterventionStartDate] = useState();
    const [interventionEndDate, setInterventionEndDate] = useState();
    const [baselineStartDate, setBaselineStartDate] = useState();
    const [baselineEndDate, setBaselineEndDate] = useState();
    const [maxDate, setMaxDate] = useState();
    const [showErrorMsg, setShowErrorMsg] = useState(false);
    const [selectedToggle, setSelectedToggle] = useState("nocturnal_hrv");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const recommendationModalRef = useRef(null);
    const [activeTab, setActiveTab] = useState(0);
    const [HRTemplateNumber, setHRTemplateNumber] = useState();
    const [HRVTemplateNumber, setHRVTemplateNumber] = useState();
    const [templateNum, setTemplateNum] = useState();
    const [showNoDataMsg, setShowNoDataMsg] = useState(false);
    const [experimentPageData, setExperimentPageData] = useState();
    const url = CONFIG.createUrl();

    const config = {headers: CONFIG.HEADERS};

    const insights = [
        {
            "text": [
                "The intervention has a substantial and statistically significant impact, indicating strong positive effects.",
                "A large effect size confirms that the changes are not only statistically significant but also of high practical relevance.",
            ]
        },
        {
            "text": [
                "The test results reveal a statistically significant difference with a moderate effect size, suggesting that the intervention had a noticeable and meaningful impact.",
                "The moderate effect size underlines the practical relevance of the intervention in real-world settings.",
            ]
        },
        {
            "text": [
                "The intervention results in statistically significant changes, though the effect size is small, indicating minimal practical benefits.",
                "While the changes are statistically real, they might not be substantial enough to be noticeable or meaningful in practical settings.",
            ]
        },
        {
            "text": [
                "A large effect size suggests significant potential benefits, but the lack of statistical significance indicates variability or possible outliers.",
                "This scenario may highlight effective conditions or periods where the intervention is effective.",
            ]
        },
        {
            "text": [
                "The results show no statistically significant difference, with a small to medium effect size, indicating that any observed changes are either inconsistent across the sample or too minor to be impactful.",
                "The lack of significant results suggests that the intervention may not have a substantial impact, or the experiment duration may be insufficient to detect a meaningful effect.",
            ]
        }
    ];

    const protocols = [
        {
            "text": [
                "Consider scaling up or continuing the intervention as it shows significant benefits.",
                "Explore additional variables that might enhance these effects or provide deeper insights into why the intervention is so effective."
            ]
        },
        {
            "text": [
                "Consider continuing the intervention to confirm these results and explore potential long-term impacts of the intervention.",
                "Investigate other contributing factors or conditions that could enhance the intervention’s effectiveness."
            ]
        },
        {
            "text": [
                "Reassess and potentially redesign the intervention to target more significant outcome improvements.",
                "Consider augmenting the intervention with additional elements or increasing its duration or intensity."
            ]
        },
        {
            "text": [
                "Consider scaling up or continuing the intervention as it shows significant benefits.",
                "Assess the effectiveness of this intervention after gathering more data to determine if its impacts remain statistically significant."
            ]
        },
        {
            "text": [
                "Reassess the intervention made or consider increasing the the experiment duration to detect potential effects.",
                "Explore different or additional interventions that might better capture the impact of the intervention and provide more definitive improvements."
            ]
        }
    ];

    const fetchExpData = async () => {
        try {
            const expResponse = await axios.get(`${url}experiment/?uuid=${userInfo.uuid}&control_start_date=${baselineStartDate}&control_end_date=${baselineEndDate}&test_start_date=${interventionStartDate}&test_end_date=${interventionEndDate}`, config);

            const expDashboardData = {
                expData: expResponse.data.data
            }

            addExpPageData(expDashboardData);
            setFetchData(prev => ({ ...prev, expDashboardData: expDashboardData }));
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }

    const fetchMaxDate = async () => {
        try {
            const maxDateResponse = await axios.get(`${url}hrv/predictions/max-day?uuid=${userInfo.uuid}`, config);
            setMaxDate(maxDateResponse.data.max_day);
            setFetchData(prev => ({ ...prev, maxDate: maxDateResponse.data.max_day }));
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }

    const getTemplateNumber = (data) => {
        const significance = data.significance;
        const effect = Math.abs(data.effect);
        if ((significance <= 0.15) && (effect >= 0.5)) {
            return 0;
        } else if ((significance <= 0.15) && (effect >= 0.2) && (effect < 0.5)) {
            return 1;
        } else if ((significance <= 0.15) && (effect < 0.2)) {
            return 2;
        } else if ((significance > 0.15) && (effect >= 0.5)) {
            return 3;
        } else if ((significance > 0.15) && (effect < 0.5)) {
            return 4;
        }
    }

    useEffect(() => {
        if (!fetchData.maxDate) {
            fetchMaxDate();
        } else {
            setMaxDate(fetchData.maxDate);
            setLoading(false);
        }
        if (fetchData.expDashboardData) {
            addExpPageData(fetchData.expDashboardData);
            setInterventionStartDate(fetchData.expPageDates.interventionStartDate);
            setInterventionEndDate(fetchData.expPageDates.interventionEndDate);
            setBaselineStartDate(fetchData.expPageDates.baselineStartDate);
            setBaselineEndDate(fetchData.expPageDates.baselineEndDate);
        }
    }, []);

    const addExpPageData = ({ expData }) => {

        if (Object.keys(expData).length === 0) {
            setShowNoDataMsg(true);
        } else {
            setShowNoDataMsg(false);
            setExperimentPageData(expData);
            setHRVTemplateNumber(getTemplateNumber(expData.nocturnal_hrv));
            setHRTemplateNumber(getTemplateNumber(expData.average_heart_rate));
        }
    }

    const validateDate = () => {
        return (interventionStartDate && interventionEndDate && baselineStartDate && baselineEndDate && (new Date(interventionStartDate) < new Date(interventionEndDate)) && (new Date(baselineStartDate) < new Date(baselineEndDate)));
    }

    const handleInterventionStartDateChange = (event) => {
        setInterventionStartDate(event.target.value);
        if (validateDate()) {
            setShowErrorMsg(false);
        }
    }

    const handleInterventionEndDateChange = (event) => {
        setInterventionEndDate(event.target.value);
        if (validateDate()) {
            setShowErrorMsg(false);
        }
    }

    const handleBaselineStartDateChange = (event) => {
        setBaselineStartDate(event.target.value);
        if (validateDate()) {
            setShowErrorMsg(false);
        }
    }

    const handleBaselineEndDateChange = (event) => {
        setBaselineEndDate(event.target.value);
        if (validateDate()) {
            setShowErrorMsg(false);
        }
    }

    const runExperiment = () => {
        if (validateDate()) {
            setShowErrorMsg(false);
            setLoading(true);
            fetchExpData();
            const expPageDates = {
                interventionStartDate: interventionStartDate,
                interventionEndDate: interventionEndDate,
                baselineStartDate: baselineStartDate,
                baselineEndDate: baselineEndDate
            }
            setFetchData(prev => ({ ...prev, expPageDates: expPageDates }));
        } else {
            setShowErrorMsg(true);
        }
    }

    const handleToggle = () => {
        setSelectedToggle(selectedToggle === "nocturnal_hrv" ? "average_heart_rate" : "nocturnal_hrv");
    };

    const calcPercentageChange = (val1, val2) => {
        const changePercentage = (((val1 - val2) / val2).toFixed(2) * 100).toFixed(0);
        let iconClassName = '';
        let textColor = ''
        if ((changePercentage > 0 && selectedToggle === 'average_heart_rate') || (changePercentage < 0 && selectedToggle === 'nocturnal_hrv')) {
            iconClassName = "fa-solid fa-caret-down arrow-icon";
            textColor = 'red';
        } else if ((changePercentage < 0 && selectedToggle === 'average_heart_rate') || (changePercentage > 0 && selectedToggle === 'nocturnal_hrv')) {
            iconClassName = "fa-solid fa-caret-up arrow-icon";
            textColor = 'green';
        }

        return (<div style={{ color: textColor }}><i className={iconClassName}></i>{Math.abs(changePercentage)}%</div>);
    }

    const openModal = () => {
        if (selectedToggle === 'nocturnal_hrv') {
            setTemplateNum(HRVTemplateNumber);
        } else {
            setTemplateNum(HRTemplateNumber);
        }
        setIsModalOpen(true);
        document.body.style.overflow = 'hidden'; // Prevent body scroll
    };

    const closeModal = () => {
        setActiveTab(0);
        setIsModalOpen(false);
        document.body.style.overflow = 'auto'; // Restore body scroll
    };

    const handleClickOutside = (event) => {
        if (recommendationModalRef.current && !recommendationModalRef.current.contains(event.target)) {
            closeModal();
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="container experiments-page">
            <div className="main-content">
                <section className="card">
                    <div className="filter-container">
                        <div>
                            <h3 className="date-range-type">Intervention Period</h3>
                            <div className="date-input-group">
                                <div className="date-input-container">
                                    <label className="input-label" htmlFor="interventionStartDate">Start Date:</label>
                                    <input className="date-input" type="date" id="interventionStartDate" name="interventionStartDate" max={maxDate} value={interventionStartDate} onChange={handleInterventionStartDateChange}></input>
                                </div>
                                <div className="date-input-container">
                                    <label className="input-label" htmlFor="interventionEndDate">End Date:</label>
                                    <input className="date-input" type="date" id="interventionEndDate" name="interventionEndDate" max={maxDate} value={interventionEndDate} onChange={handleInterventionEndDateChange}></input>
                                </div>
                            </div>
                            <h3 className="date-range-type">Baseline Period:</h3>
                            <div className="date-input-group">
                                <div className="date-input-container">
                                    <label className="input-label" htmlFor="baselineStartDate">Start Date:</label>
                                    <input className="date-input" type="date" id="baselineStartDate" name="baselineStartDate" max={maxDate} value={baselineStartDate} onChange={handleBaselineStartDateChange}></input>
                                </div>
                                <div className="date-input-container">
                                    <label className="input-label" htmlFor="baselineEndDate">End Date:</label>
                                    <input className="date-input" type="date" id="baselineEndDate" name="baselineEndDate" max={maxDate} value={baselineEndDate} onChange={handleBaselineEndDateChange}></input>
                                </div>
                            </div>
                        </div>
                        <div style={{ position: 'relative' }}>
                            <button className="generate-button run-exp-btn" onClick={runExperiment}>Run Experiment</button>
                            {showErrorMsg &&
                                <div className="error-msg">Enter valid dates to run experiment.</div>
                            }
                        </div>
                    </div>
                    {loading &&
                        <div className="spinner-container">
                            <ClipLoader color={"#123abc"} loading={loading} size={50} />
                        </div>
                    }
                    {!loading && showNoDataMsg &&
                        <div className="tab-content experiments-tab-content no-data-text">
                            No data recorded in this period.
                        </div>
                    }
                    {!loading && !showNoDataMsg && experimentPageData &&
                        <div className="tab-content experiments-tab-content">
                            <div className="toggle-container" onClick={handleToggle}>
                                <div className={`toggle-slider ${selectedToggle === "average_heart_rate" ? "toggled" : ""}`}>
                                    {selectedToggle === "nocturnal_hrv" ? "HRV" : "HR"}
                                </div>
                                <div className="toggle-labels">
                                    <span className={selectedToggle === "nocturnal_hrv" ? "active" : ""}>HRV</span>
                                    <span className={selectedToggle === "average_heart_rate" ? "active" : ""}>HR</span>
                                </div>
                            </div>
                            <button className="ai-recco-button" onClick={openModal}>Concierge Insights</button>
                            <div>
                                <table className="exp-table">
                                    <thead>
                                        <tr>
                                            <th>Metric</th>
                                            <th>Intervention Period</th>
                                            <th>Baseline Period</th>
                                            <th>Change</th>
                                            <th>% Change</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Average HRV</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.test.mean.toFixed(0)}</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.control.mean.toFixed(0)}</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.test.mean.toFixed(0) - experimentPageData[selectedToggle].breakdown_stats.control.mean.toFixed(0)}</td>
                                            <td>{calcPercentageChange(experimentPageData[selectedToggle].breakdown_stats.test.mean, experimentPageData[selectedToggle].breakdown_stats.control.mean)}</td>
                                        </tr>
                                        <tr>
                                            <td>HRV Variability</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.test.std.toFixed(1)}</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.control.std.toFixed(1)}</td>
                                            <td>{(experimentPageData[selectedToggle].breakdown_stats.test.std.toFixed(1) - experimentPageData[selectedToggle].breakdown_stats.control.std.toFixed(1)).toFixed(1)}</td>
                                            <td>{calcPercentageChange(experimentPageData[selectedToggle].breakdown_stats.test.std, experimentPageData[selectedToggle].breakdown_stats.control.std)}</td>
                                        </tr>
                                        <tr className="header-row">
                                            <td>Quartiles</td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                        </tr>
                                        <tr>
                                            <td className="sub-header-metric">Min</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.test.min.toFixed(0)}</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.control.min.toFixed(0)}</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.test.min.toFixed(0) - experimentPageData[selectedToggle].breakdown_stats.control.min.toFixed(0)}</td>
                                            <td>{calcPercentageChange(experimentPageData[selectedToggle].breakdown_stats.test.min, experimentPageData[selectedToggle].breakdown_stats.control.min)}</td>
                                        </tr>
                                        <tr>
                                            <td className="sub-header-metric">25th percentile</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.test["25%"].toFixed(0)}</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.control["25%"].toFixed(0)}</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.test["25%"].toFixed(0) - experimentPageData[selectedToggle].breakdown_stats.control["25%"].toFixed(0)}</td>
                                            <td>{calcPercentageChange(experimentPageData[selectedToggle].breakdown_stats.test["25%"], experimentPageData[selectedToggle].breakdown_stats.control["25%"])}</td>
                                        </tr>
                                        <tr>
                                            <td className="sub-header-metric">50th percentile</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.test["50%"].toFixed(0)}</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.control["50%"].toFixed(0)}</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.test["50%"].toFixed(0) - experimentPageData[selectedToggle].breakdown_stats.control["50%"].toFixed(0)}</td>
                                            <td>{calcPercentageChange(experimentPageData[selectedToggle].breakdown_stats.test["50%"], experimentPageData[selectedToggle].breakdown_stats.control["50%"])}</td>
                                        </tr>
                                        <tr>
                                            <td className="sub-header-metric">75th percentile</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.test["75%"].toFixed(0)}</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.control["75%"].toFixed(0)}</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.test["75%"].toFixed(0) - experimentPageData[selectedToggle].breakdown_stats.control["75%"].toFixed(0)}</td>
                                            <td>{calcPercentageChange(experimentPageData[selectedToggle].breakdown_stats.test["75%"], experimentPageData[selectedToggle].breakdown_stats.control["75%"])}</td>
                                        </tr>
                                        <tr>
                                            <td className="sub-header-metric">Max</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.test.max.toFixed(0)}</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.control.max.toFixed(0)}</td>
                                            <td>{experimentPageData[selectedToggle].breakdown_stats.test.max.toFixed(0) - experimentPageData[selectedToggle].breakdown_stats.control.max.toFixed(0)}</td>
                                            <td>{calcPercentageChange(experimentPageData[selectedToggle].breakdown_stats.test.max, experimentPageData[selectedToggle].breakdown_stats.control.max)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            {isModalOpen && (
                                <div className="modal-overlay">
                                    <div className="modal-content recommendation-modal" ref={recommendationModalRef}>
                                        <button className="close-modal-button" onClick={closeModal}>×</button>
                                        <div className="dashboard-carousel">
                                            <h4 className="recommendation-header">CONCIERGE INSIGHTS</h4>
                                            <div className="tabs">
                                                <button
                                                    className={activeTab === 0 ? 'active' : ''}
                                                    onClick={() => setActiveTab(0)}
                                                >
                                                    Analysis of Today's Insights
                                                </button>
                                            </div>
                                            <div className="tab-content">
                                                {activeTab === 0 &&
                                                    <>
                                                        <h4>Insights from our AI algorithm:</h4>
                                                        <ul className="analysis-ul">
                                                            {insights[templateNum].text.map((insight, index) => (
                                                                <li key={`experiment-insight-${index}`}>
                                                                    {insight}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                        <h4>Elite Performance Protocol</h4>
                                                        <ul className="analysis-ul">
                                                            {protocols[templateNum].text.map((protocol, index) => (
                                                                <li key={`experiment-protocol-${index}`}>
                                                                    {protocol}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    }
                </section>
            </div>
        </div>
    );
}

export default Experiments;